/**
 * Facility Context Hook
 *
 * Provides smart facility selection with:
 * - Shift-based defaults (medical/care staff on shift)
 * - Grant-based defaults (external auditors)
 * - User preference persistence (localStorage)
 * - Manual facility switching
 *
 * Priority order:
 * 1. User's manual selection (if valid)
 * 2. Current shift facility (if on shift)
 * 3. Backend default (grant or first accessible)
 * 4. First accessible facility (fallback)
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuth } from './useAuth';
import type { CurrentShiftInfo } from '@/types/auth';
import { useMemo } from 'react';
import { setFacilityContextSignal } from '@/lib/facilityContextSignal';

interface FacilityContextState {
  // User's manually selected facility (persisted)
  selectedFacilityId: string | null;

  // Actions
  setFacility: (facilityId: string | null) => void;
  clearFacility: () => void;
}

/**
 * Zustand store for facility selection with localStorage persistence
 */
const useFacilityStore = create<FacilityContextState>()(
  persist(
    (set) => ({
      selectedFacilityId: null,

      setFacility: (facilityId) => set({ selectedFacilityId: facilityId }),

      clearFacility: () => set({ selectedFacilityId: null }),
    }),
    {
      name: 'facility-context-storage', // localStorage key
    }
  )
);

/**
 * Reset the manual facility selection. FOR TESTS ONLY.
 *
 * This is a module-scoped Zustand store, so its state is a singleton for the
 * lifetime of the module — exactly what we want in the browser, and exactly
 * what leaks between tests. A test that selects a facility leaves it selected
 * for every later test in the file, which then resolves the wrong facility.
 *
 * Clearing localStorage is NOT sufficient: the store is already hydrated in
 * memory, so the persisted key is irrelevant once it has been read.
 */
export function __resetFacilitySelectionForTests(): void {
  useFacilityStore.setState({ selectedFacilityId: null });
}

/**
 * Hook return type with all facility context data and helpers
 */
interface UseFacilityContextReturn {
  // Current effective facility ID (result of resolution logic)
  currentFacilityId: string | null;

  // All accessible facility IDs
  facilityIds: string[];

  // Shift information (if on shift)
  currentShift: CurrentShiftInfo | null;
  isOnShift: boolean;

  // Backend default facility
  defaultFacilityId: string | null;
  facilityContextSource: 'shift' | 'grant' | 'default';

  // Whether current facility is from shift (not manual selection)
  isShiftBased: boolean;

  // Actions (with audit logging)
  setFacility: (facilityId: string | null) => void;
  clearFacility: () => void;

  // Loading state
  isLoading: boolean;
}

/**
 * Log facility context change for audit trail
 */

/**
 * Main facility context hook
 *
 * Usage:
 * ```tsx
 * const { currentFacilityId, setFacility, isOnShift, currentShift } = useFacilityContext();
 *
 * // Use currentFacilityId for data fetching
 * const { data: residents } = useResidents({ facility_id: currentFacilityId });
 *
 * // Allow manual switching
 * <FacilitySelector onChange={setFacility} />
 * ```
 */
export function useFacilityContext(): UseFacilityContextReturn {
  // Get auth context (includes shift info and backend default)
  const { data: authContext, isLoading } = useAuth();

  // Get user's manual selection from store
  const { selectedFacilityId, setFacility: setFacilityRaw, clearFacility: clearFacilityRaw } = useFacilityStore();


  // Extract facility context from auth (memoized for stable reference)
  const facilityIds = useMemo(() => authContext?.facilityIds || [], [authContext?.facilityIds]);
  const currentShift = authContext?.currentShift || null;
  const isOnShift = authContext?.isOnShift || false;
  const defaultFacilityId = authContext?.defaultFacilityId || null;
  const facilityContextSource = authContext?.facilityContextSource || 'default';

  /**
   * Smart facility resolution logic:
   * 1. User's manual selection (if still valid)
   * 2. Current shift facility (if on shift)
   * 3. Backend default (grant-based or default)
   * 4. First accessible facility (fallback)
   */
  let currentFacilityId: string | null = null;
  let isShiftBased = false;

  // Priority 1: User's manual selection (if valid)
  if (selectedFacilityId && facilityIds.includes(selectedFacilityId)) {
    currentFacilityId = selectedFacilityId;
  }
  // Priority 2: Current shift facility (if on shift)
  else if (isOnShift && currentShift) {
    currentFacilityId = currentShift.facilityId;
    isShiftBased = true;
  }
  // Priority 3: Backend default
  else if (defaultFacilityId) {
    currentFacilityId = defaultFacilityId;
  }
  // Priority 4: First accessible facility (fallback)
  else if (facilityIds.length > 0) {
    currentFacilityId = facilityIds[0];
  }

  // If user's selection is no longer valid, clear it
  if (selectedFacilityId && !facilityIds.includes(selectedFacilityId)) {
    clearFacilityRaw();
  }

  // US-179: publish the RESOLVED context so apiClient can attach it to every
  // request as X-Facility-Context, which is what lets the backend record a
  // facility switch without a separate call that can fail silently (F-013).
  // Assigned during render, matching the ref update immediately above — a
  // useEffect would lag by one render and the first request after a switch
  // would carry the stale value. `''` is the explicit "All facilities" choice
  // and must stay distinguishable from "nothing selected".
  setFacilityContextSignal(
    selectedFacilityId === '' ? 'all' : currentFacilityId
  );

  // US-179: these were wrappers that POSTed each change to
  // /auth/facility-context-change for the audit trail. That call is gone — the
  // context now rides on every request as a header and the backend records the
  // change — so the store actions are exposed directly rather than wrapped in
  // callbacks that add nothing. Zustand actions are already stable references.
  const setFacility = setFacilityRaw;
  const clearFacility = clearFacilityRaw;

  return {
    currentFacilityId,
    facilityIds,
    currentShift,
    isOnShift,
    defaultFacilityId,
    facilityContextSource,
    isShiftBased,
    setFacility,
    clearFacility,
    isLoading,
  };
}

/**
 * Helper hook to check if user should be locked to shift facility
 * (e.g., for displaying warnings or disabling facility switcher)
 */
export function useIsShiftLocked(): boolean {
  const { isOnShift, currentShift, isShiftBased } = useFacilityContext();
  return isOnShift && currentShift !== null && isShiftBased;
}
